import {
  DARK_MODE_MEDIA_QUERY,
  THEME_ATTRIBUTE,
  THEME_PREFERENCES,
  THEME_STORAGE_KEY,
} from './colorMode.constants';
import type { ResolvedTheme, ThemePreference } from './colorMode.constants';

export const isThemePreference = (value: unknown): value is ThemePreference =>
  typeof value === 'string' &&
  (THEME_PREFERENCES as readonly string[]).includes(value);

export const resolveTheme = (
  preference: ThemePreference,
  systemPrefersDark: boolean
): ResolvedTheme => {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
};

export const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DARK_MODE_MEDIA_QUERY).matches;
};

// localStorage can throw (private mode, blocked storage) — the theme is a
// convenience, so every access fails soft.
export const readStoredPreference = (): ThemePreference | null => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const writeStoredPreference = (preference: ThemePreference) => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Ignore: the server preference still carries the choice.
  }
};

export const clearStoredPreference = () => {
  try {
    window.localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Ignore.
  }
};

/** `null` removes the attribute, which is the plain light app. */
export const applyThemeToDocument = (theme: ResolvedTheme | null) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute(THEME_ATTRIBUTE, 'dark');
  } else {
    root.removeAttribute(THEME_ATTRIBUTE);
  }
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

type DocumentWithViewTransition = Document & {
  startViewTransition?: (update: () => void) => unknown;
};

/**
 * Runs a theme change as a crossfade (View Transitions API) instead of an
 * instant repaint. `update` must apply the change to the DOM synchronously
 * (the browser snapshots before and after it). Falls back to running it
 * directly when the API is missing or the user prefers reduced motion.
 */
export const runThemeTransition = (update: () => void) => {
  const doc = document as DocumentWithViewTransition;
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches;
  if (typeof doc.startViewTransition !== 'function' || prefersReducedMotion) {
    update();
    return;
  }
  doc.startViewTransition(update);
};
